import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotification } from '../../../../../contexts/NotificationContext';
import { useConfirm } from '../../../../../contexts/ConfirmContext';
import { useAuth } from '../../../../../contexts/AuthContext';
import apiService from '../../../../../services/api';
import SidePanel from '../../../../UI/SidePanel';
import CustomDropdown from '../../../../UI/CustomDropdown';
import SkeletonLoader from '../../../../UI/SkeletonLoader';
import EmptyState from '../../../../UI/EmptyState';
import { GroupMember, UserFilters, UserManagementTabProps } from './types';
import UserFiltersComponent from './components/UserFilters';
import UserList from './components/UserList';
import SelectionBanner from './components/SelectionBanner';
import { getInitials, formatDate } from './components/helpers';
import buttonStyles from '../../../../../styles/Buttons.module.css';
import sidePanelStyles from '../../../../../styles/SidePanel.module.css';
import customDropdownStyles from '../../../../../styles/CustomDropdown.module.css';
import styles from '../../../../../styles/PointScheme.module.css';
import userManagementStyles from '../../../../../styles/UserManagementTab.module.css';
import searchStyles from '../../../../../styles/SearchBar.module.css';

const UserManagementTab: React.FC<UserManagementTabProps> = ({
  groupDetails,
  onGroupUpdate,
  hasPermission = () => false,
  isAdminContext = true
}) => {
  const notificationService = useNotification();
  const confirmService = useConfirm();
  const { user } = useAuth();

  // State management
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());

  // Filters
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    status: ''
  });

  // Form states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);

  // Add/Edit form
  const [addForm, setAddForm] = useState({
    user_id: '',
    email: '',
    role: 'member'
  });

  const [editForm, setEditForm] = useState({
    role: 'member',
    status: 'active',
    notes: ''
  });

  const isSystemAdmin = user?.role === 'admin';

  // Determine if current user is group admin
  const isGroupAdmin = React.useMemo(() => {
    if (!user || !members.length) return false;
    const currentUserMember = members.find(m => m.user.id === user.id);
    return currentUserMember ? (currentUserMember.role_type === 'admin' || currentUserMember.is_main_admin === true) : false;
  }, [user, members]);

  const canManageUsers = isSystemAdmin || isGroupAdmin;

  // Search
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');

  // Refs for tracking
  const hasInitiallyLoaded = useRef(false);
  const hasFailedToLoad = useRef(false);
  const previousGroupId = useRef<string | null>(null);
  const isLoadingInitialData = useRef(false);

  // Load members
  const loadMembers = useCallback(async () => {
    if (isLoadingMembers) return;

    setIsLoadingMembers(true);
    try {
      const response = await apiService.getGroupMembers(groupDetails.id, {
        search: filters.search,
        role: filters.role,
        status: filters.status
      });

      if (response.status === 'success') {
        setMembers(response.data || []);
      } else {
        notificationService.error(response.message || 'Failed to load members');
      }
    } catch (error: any) {
      console.error('Error loading members:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load members. Please try again.');
    } finally {
      setIsLoadingMembers(false);
    }
  }, [groupDetails.id, filters, isLoadingMembers, notificationService]);


  // Search users
  const searchUsers = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiService.searchUsers(query);
      if (response.status === 'success') {
        const memberUserIds = members.map(m => m.user.id);
        const filteredResults = (response.data || []).filter(
          (user: any) => !memberUserIds.includes(user.id)
        );
        setSearchResults(filteredResults);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [members]);

  const handleSearch = (value: string) => {
    setFilters((prev: UserFilters) => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key: keyof UserFilters, value: string) => {
    setFilters((prev: UserFilters) => ({ ...prev, [key]: value }));
  };

  const handleMainSearchChange = (value: string) => {
    setFilters((prev: UserFilters) => ({ ...prev, search: value }));
  };

  const handleSearchInput = (value: string) => {
    setSearchInputValue(value);
    searchUsers(value);
  };

  const selectUser = (user: any) => {
    setAddForm(prev => ({ ...prev, user_id: user.id, email: user.email }));
    setSearchInputValue('');
    setShowSearchResults(false);
    setSearchResults([]);
  };

  const openAddModal = () => {
    setAddForm({ user_id: '', email: '', role: 'member' });
    setSearchInputValue('');
    setShowSearchResults(false);
    setShowAddModal(true);
  };

  const openEditModal = (member: GroupMember) => {
    setSelectedMember(member);
    setEditForm({
      role: member.role_type,
      status: member.status || (member.is_active ? 'active' : 'inactive'),
      notes: member.notes || ''
    });
    setShowEditModal(true);
  };


  const handleAddMember = async () => {
    if (isAddingMember) return;

    if (!addForm.user_id && !addForm.email) {
      notificationService.error('Please select a user or enter an email address');
      return;
    }

    if (!addForm.role) {
      notificationService.error('Please select a role');
      return;
    }

    // Backend API expects email and role_id, not user_id and role
    if (!addForm.email) {
      notificationService.error('Email is required');
      return;
    }

    setIsAddingMember(true);
    try {
      const requestData = {
        email: addForm.email.trim(),
        role_id: addForm.role
      };

      const response = await apiService.addGroupMember(groupDetails.id, requestData);

      if (response.status === 'success') {
        notificationService.success('Member added successfully');
        setShowAddModal(false);
        setAddForm({ user_id: '', email: '', role: 'member' });
        setSearchInputValue('');
        setShowSearchResults(false);
        loadMembers();
        onGroupUpdate();
      } else {
        notificationService.error(response.message || 'Failed to add member');
      }
    } catch (error: any) {
      console.error('Error adding member:', error);
      notificationService.error(error.response?.data?.message || 'Failed to add member. Please try again.');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember || isUpdatingMember) return;

    setIsUpdatingMember(true);
    try {
      const response = await apiService.updateGroupMember(selectedMember.id, groupDetails.id, {
        role: editForm.role,
        status: editForm.status,
        notes: editForm.notes
      });

      if (response.status === 'success') {
        notificationService.success('Member updated successfully');
        setShowEditModal(false);
        loadMembers();
      } else {
        notificationService.error(response.message || 'Failed to update member');
      }
    } catch (error: any) {
      console.error('Error updating member:', error);
      notificationService.error(error.response?.data?.message || 'Failed to update member. Please try again.');
    } finally {
      setIsUpdatingMember(false);
    }
  };

  const handleRemoveMember = async (member: GroupMember) => {
    const confirmed = await confirmService.show(
      'Remove Member',
      `Are you sure you want to remove "${member.user.full_name || `${member.user.first_name} ${member.user.last_name}`}" from this group?`,
      { okText: 'Remove', cancelText: 'Cancel' }
    );

    if (confirmed) {
      setLoading(true);
      try {
        const response = await apiService.removeGroupMember(member.id, groupDetails.id);
        if (response.status === 'success') {
          notificationService.success('Member removed successfully');
          // Update local state immediately
          setMembers(prev => prev.filter(m => m.id !== member.id));
          setSelectedMemberIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(member.id);
            return newSet;
          });
          // Reload to ensure data is in sync
          await loadMembers();
          onGroupUpdate();
        } else {
          notificationService.error(response.message || 'Failed to remove member');
        }
      } catch (error: any) {
        console.error('Error removing member:', error);
        notificationService.error(error.response?.data?.message || 'Failed to remove member. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkAction = async (action: 'remove' | 'activate' | 'deactivate' | 'change_role') => {
    if (selectedMemberIds.size === 0) {
      notificationService.error('Please select members first');
      return;
    }

    if (action === 'change_role') {
      notificationService.success('Bulk role change functionality will be implemented');
      return;
    }

    const actionText = action === 'remove' ? 'remove' : action === 'activate' ? 'activate' : 'deactivate';
    const confirmed = await confirmService.show(
      `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Members`,
      `Are you sure you want to ${actionText} ${selectedMemberIds.size} member(s)?`,
      { okText: actionText.charAt(0).toUpperCase() + actionText.slice(1), cancelText: 'Cancel' }
    );

    if (confirmed) {
      setLoading(true);
      try {
        const response = await apiService.bulkUpdateMembers(groupDetails.id, {
          member_ids: Array.from(selectedMemberIds),
          action: action as 'remove' | 'activate' | 'deactivate'
        });

        if (response.status === 'success') {
          notificationService.success(`Members ${actionText}d successfully`);
          setSelectedMemberIds(new Set());
          // Update local state immediately for remove action
          if (action === 'remove') {
            setMembers(prev => prev.filter(m => !selectedMemberIds.has(m.id)));
          }
          await loadMembers();
          onGroupUpdate();
        } else {
          notificationService.error(response.message || `Failed to ${actionText} members`);
        }
      } catch (error: any) {
        console.error(`Error ${actionText}ing members:`, error);
        notificationService.error(error.response?.data?.message || `Failed to ${actionText} members. Please try again.`);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleMemberSelection = (memberId: string) => {
    const newSelected = new Set(selectedMemberIds);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMemberIds(newSelected);
  };

  const selectAllMembers = () => {
    if (selectedMemberIds.size === members.length) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(members.map(m => m.id)));
    }
  };


  // Effects
  useEffect(() => {
    const groupHasChanged = previousGroupId.current !== groupDetails.id;

    if (groupHasChanged) {
      hasInitiallyLoaded.current = false;
      hasFailedToLoad.current = false;
      isLoadingInitialData.current = false;
      previousGroupId.current = groupDetails.id;
    }

    const loadInitialData = async () => {
      if (isLoadingInitialData.current || hasInitiallyLoaded.current || hasFailedToLoad.current) {
        return;
      }

      isLoadingInitialData.current = true;

      try {
        setLoading(true);
        await loadMembers();

        hasInitiallyLoaded.current = true;
      } catch (error: any) {
        console.error('Error loading initial data:', error);
        notificationService.error((error as any).response?.data?.message || 'Failed to load user management data');
        hasFailedToLoad.current = true;
      } finally {
        setLoading(false);
        isLoadingInitialData.current = false;
      }
    };

    loadInitialData();
  }, [groupDetails.id]);

  useEffect(() => {
    if (hasFailedToLoad.current) return;

    const timeoutId = setTimeout(() => {
      loadMembers();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters.search, filters.role, filters.status]);

  // Filter options
  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Group Admin' },
    { value: 'member', label: 'Member' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const editStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' }
  ];

  return (
    <div className={styles.pointSchemeContainer}>
      {/* Header - Single Line */}
      <div className={styles.headerContent} style={{ padding: '0 1rem 2rem 1rem' }}>
        <UserFiltersComponent
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearchChange={handleMainSearchChange}
          onRefresh={loadMembers}
          onAddMember={openAddModal}
          roleOptions={roleOptions}
          statusOptions={statusOptions}
          canManageUsers={canManageUsers}
        />
      </div>

      {/* Selection Banner */}
      <SelectionBanner
        selectedCount={selectedMemberIds.size}
        totalCount={members.length}
        onSelectAll={selectAllMembers}
        onClearSelection={() => setSelectedMemberIds(new Set())}
        onBulkAction={handleBulkAction}
        canManageUsers={canManageUsers}
      />

      {/* Members Section */}
      <div className={`${styles.categoriesSection} ${styles.noMargins}`}>
        {hasFailedToLoad.current ? (
          <EmptyState
            icon="⚠️"
            title="Failed to Load Data"
            description="Unable to load user management data. Please check your connection and try again."
            action={{
              label: "Retry Loading",
              onClick: () => {
                hasFailedToLoad.current = false;
                hasInitiallyLoaded.current = false;
                loadMembers();
              }
            }}
          />
        ) : (
          <UserList
            members={members}
            loading={loading}
            selectedMemberIds={selectedMemberIds}
            onSelectMember={toggleMemberSelection}
            onEditMember={openEditModal}
            onDeleteMember={handleRemoveMember}
            groupId={groupDetails.id}
            canManageUsers={canManageUsers}
            onAddMember={openAddModal}
          />
        )}
      </div>

      {/* Add Member Side Panel */}
      <SidePanel
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Search User"
      >
        <div className={sidePanelStyles.form}>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Search User
            </label>
            <div className={userManagementStyles.searchContainer}>
              <div className={sidePanelStyles.formControl}>
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={searchInputValue}
                  onChange={(e) => handleSearchInput(e.target.value)}
                />
              </div>

              {showSearchResults && (
                <div className={`${userManagementStyles.searchResults} ${showSearchResults ? userManagementStyles.active : ''}`}>
                  {searchLoading ? (
                    <div style={{ padding: '1rem', textAlign: 'center' }}>
                      <SkeletonLoader count={1} variant="card" />
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map(user => (
                      <div
                        key={user.id}
                        className={userManagementStyles.searchResultItem}
                        onClick={() => selectUser(user)}
                      >
                        <div className={userManagementStyles.searchResultAvatar}>
                          {getInitials(user.first_name, user.last_name)}
                        </div>
                        <div className={userManagementStyles.searchResultInfo}>
                          <div className={userManagementStyles.searchResultName}>
                            {user.full_name || `${user.first_name} ${user.last_name}`}
                          </div>
                          <div className={userManagementStyles.searchResultEmail}>{user.email}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray)' }}>
                      No users found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: '70%',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: '1rem 0'
            }}>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: 'var(--border)'
              }}></div>
              <div style={{
                color: 'var(--gray)',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>or</div>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: 'var(--border)'
              }}></div>
            </div>
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              className={sidePanelStyles.formControl}
              placeholder="Enter email address"
              value={addForm.email}
              onChange={(e) => {
                setAddForm(prev => ({ ...prev, email: e.target.value, user_id: '' }));
                setSearchInputValue('');
                setShowSearchResults(false);
              }}
              required
            />
            <small className={sidePanelStyles.formText}>
              Enter the email address of the user to add
            </small>
          </div>

          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>
              Role <span className="required">*</span>
            </label>
            <CustomDropdown
              options={roleOptions.filter(opt => opt.value !== '')}
              value={addForm.role}
              onChange={(value: string | string[]) => setAddForm(prev => ({ ...prev, role: Array.isArray(value) ? value[0] : value }))}
              placeholder="Select Role"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
            />
            <small className={sidePanelStyles.formText}>
              Select the role for this member
            </small>
          </div>

          <div className={sidePanelStyles.formActions}>
            <button
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => setShowAddModal(false)}
              disabled={isAddingMember}
            >
              Cancel
            </button>
            <button
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
              onClick={handleAddMember}
              disabled={isAddingMember}
            >
              {isAddingMember ? (
                <>
                  <svg className={styles.spinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Adding...
                </>
              ) : (
                'Add Member'
              )}
            </button>
          </div>
        </div>
      </SidePanel>

      {/* Edit Member Side Panel */}
      <SidePanel
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Member"
      >
        {selectedMember && (
          <div className={sidePanelStyles.form}>
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Member Information
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--gray-lighter)',
                borderRadius: '8px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  {getInitials(selectedMember.user.first_name, selectedMember.user.last_name)}
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: 'var(--dark)'
                  }}>
                    {selectedMember.user.full_name || `${selectedMember.user.first_name} ${selectedMember.user.last_name}`}
                  </h4>
                  <p style={{
                    margin: '0 0 0.25rem 0',
                    fontSize: '0.9rem',
                    color: 'var(--gray)'
                  }}>
                    {selectedMember.user.email}
                  </p>
                  <span style={{
                    fontSize: '0.8rem',
                    color: 'var(--gray)',
                    fontFamily: 'monospace'
                  }}>
                    ID: {selectedMember.user.id}
                  </span>
                </div>
              </div>
            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Role <span className="required">*</span>
              </label>
              <CustomDropdown
                options={roleOptions.filter(opt => opt.value !== '')}
                value={editForm.role}
                onChange={(value: string | string[]) => setEditForm(prev => ({ ...prev, role: Array.isArray(value) ? value[0] : value }))}
                placeholder="Select Role"
                size="md"
                variant="outlined"
                className={customDropdownStyles.sizeFull}
              />
              <small className={sidePanelStyles.formText}>
                Select the role for this member
              </small>
            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Status <span className="required">*</span>
              </label>
              <CustomDropdown
                options={editStatusOptions}
                value={editForm.status}
                onChange={(value: string | string[]) => setEditForm(prev => ({ ...prev, status: Array.isArray(value) ? value[0] : value }))}
                placeholder="Select Status"
                size="md"
                variant="outlined"
                className={customDropdownStyles.sizeFull}
              />
              <small className={sidePanelStyles.formText}>
                Select the status for this member
              </small>
            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Member Since
              </label>
              <input
                type="text"
                className={sidePanelStyles.formControl}
                value={formatDate(selectedMember.joined_at)}
                readOnly
                style={{ backgroundColor: 'var(--gray-lighter)', cursor: 'not-allowed' }}
              />
            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Last Active
              </label>
              <input
                type="text"
                className={sidePanelStyles.formControl}
                value={selectedMember.last_active ? formatDate(selectedMember.last_active) : 'Never'}
                readOnly
                style={{ backgroundColor: 'var(--gray-lighter)', cursor: 'not-allowed' }}
              />
            </div>

            <div className={sidePanelStyles.formActions}>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={() => setShowEditModal(false)}
                disabled={isUpdatingMember}
              >
                Cancel
              </button>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={handleUpdateMember}
                disabled={isUpdatingMember}
              >
                {isUpdatingMember ? (
                  <>
                    <svg className={styles.spinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default UserManagementTab;

